import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { MenuTable } from '@ge/components/menu';
import { Dialog } from '@ge/components/modal';
import { DateTimeFormats } from '@ge/models/constants';
import { AlertIcon } from '@ge/shared/components/alerts/alert-icon';
import { isInvalid } from '@ge/shared/util/alert-utils';
import { killEventPropagation } from '@ge/shared/util/general';

const StyledDialog = styled(Dialog)`
  &.active-alert {
    margin-left: 75%;
  }
  &.site-asset {
    position: absolute;
    margin-left: 78%;
    top: 43%;
  }
`;

const StyledThead = styled.tr`
  && > th {
    font-size: 10px;
    font-weight: 500;
    padding: 6px;
  }
  .alert {
    padding-right: 0;
  }
  .start-time {
    padding-left: 5px;
  }
`;

const StyledTr = styled.tr`
  td {
    font-size: 11px;
    font-weight: ${(props) => (props.isBold ? 700 : 300)};
    div {
      text-overflow: ellipsis;
      text-transform: none;
      overflow: hidden;
      white-space: nowrap;
    }
    &:first-of-type {
      display: flex;
      align-items: center;
      div {
        flex: 1;
        max-width: 100px; // Should change based on the FD designs
        margin-right: 11px;
      }
    }
    &:nth-of-type(2) {
      div {
        max-width: 200px;
      }
    }
    &:last-of-type {
      div {
        text-align: right;
        padding-right: 11px;
      }
    }
    &.alert {
      justify-content: center;
      padding-right: 0;
      & > div {
        margin: 0 0 0 4px;
      }
    }
    .duration {
      display: flex;
      justify-content: flex-end;
    }
  }
`;

export const AlertHeaderIconMenu = ({
  alerts,
  children,
  openAlertDialog,
  activeAlerts,
  isSiteAssetTable,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation(['alerts'], { useSuspense: false });

  const handleShowMenu = (e) => {
    killEventPropagation(e);
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    killEventPropagation(e);
    setAnchorEl(null);
  };

  const getStartTime = (val) => dayjs(val).format(DateTimeFormats.ALERT_START_TIME);

  return (
    <div onClick={handleShowMenu}>
      {children}
      <StyledDialog
        className={activeAlerts ? 'active-alert' : isSiteAssetTable ? 'site-asset' : ''}
        isOpen={Boolean(anchorEl)}
        onClose={handleMenuClose}
        header={t('table.active_alerts', 'Active Alerts')}
        contentWidth
        padContent={false}
      >
        <MenuTable>
          <table>
            <thead>
              <StyledThead>
                <th className="alert">{t('table.alert', 'Alert')}</th>
                <th>{t('table.type', 'Type')}</th>
                <th className="start-time">{t('table.start_time', 'Start Time')}</th>
              </StyledThead>
            </thead>
            <tbody>
              {alerts?.map((alert, id) => (
                <StyledTr
                  key={id}
                  onClick={(e) => {
                    openAlertDialog(e, alert);
                    handleMenuClose(e);
                  }}
                >
                  <td className="alert">
                    <div>
                      <AlertIcon
                        type={alert.type}
                        invalid={isInvalid(alert)}
                        large={true}
                        isGroup={alert?.isGroup}
                        alert={alert}
                      />
                    </div>
                  </td>
                  <td>
                    <div>{t(`form.type.options.${alert.type}`, '')}</div>
                  </td>
                  <td>
                    {/* Todo: Update to dynamic value when we figure out what these are coming from */}
                    <div>{getStartTime(alert?.auditProperties?.createdDate)}</div>
                  </td>
                </StyledTr>
              ))}
            </tbody>
          </table>
        </MenuTable>
      </StyledDialog>
    </div>
  );
};

AlertHeaderIconMenu.propTypes = {
  alerts: PropTypes.instanceOf(Object).isRequired,
  children: PropTypes.node,
  containerRef: PropTypes.instanceOf(Object),
  openAlertDialog: PropTypes.func,
  activeAlerts: PropTypes.bool,
  isSiteAssetTable: PropTypes.bool,
};

AlertHeaderIconMenu.defaultValues = {
  openAlertDialog: () => null,
};
